import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PermissionDefinitionDto } from '@app/apiModules/permissionDefinitionDto';
import { RoleDto } from '@app/apiModules/roleDtoPagedResultDto';
import { RolesManagementMainComponent } from '@app/modules/administrator/roles-management-main/roles-management-main.component';
import { AlertService } from '@app/shared/services/alert.service';
import { EventEmitterService } from '@app/shared/services/eventemitter.service';
import { LoaderService } from '@app/shared/services/loader.service';
import { PermissionService } from '@app/shared/services/PermissionService';
import { RoleService } from '@app/shared/services/role.service';
import { ripple, textField, formField } from 'material-components-web';
import { firstValueFrom } from 'rxjs';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { TreeviewComponent } from '@app/shared/shared-components/treeview/treeview.component';
import { CommonModule } from '@angular/common';

export interface PermissionNode {
  name: string;
  displayName: string;
  isGranted: boolean;
  children?: PermissionNode[];
}

export interface PermissionFlatNode {
  name: string;
  displayName: string;
  level: number;
  expandable: boolean;
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
}

@Component({
    standalone: true,
    selector: 'app-add-role',
    templateUrl: './add-role.component.html',
    styleUrl: './add-role.component.scss',
    imports: [MatTreeModule, MatIconModule, FormsModule, ReactiveFormsModule, TreeviewComponent, CommonModule]
})
export class AddRoleComponent implements OnInit, OnChanges, AfterViewInit {
  roleForm: UntypedFormGroup;
  isActive = false;
  @Input() public data: RoleDto;
  @Input() public defaultPermissions: PermissionDefinitionDto[];
  @Input() public casePlaceholder: any;
  @Output() closeSlider: EventEmitter<any> = new EventEmitter();
  @Output() selectedChange = new EventEmitter<any>();
  @ViewChild('submitButton') submitButton: ElementRef;
  isHost = false;
  submitted = false;
  selectedPermissions: any;
  filterText = '';
  loading: boolean;
  isAdminRoles = false;
  allChecked = false;
  allIndeterminate = false;

  // mat-tree setup
  treeControl: FlatTreeControl<PermissionFlatNode>;
  treeFlattener: MatTreeFlattener<PermissionNode, PermissionFlatNode>;
  dataSource: MatTreeFlatDataSource<PermissionNode, PermissionFlatNode>;
  flatNodeMap = new Map<PermissionFlatNode, PermissionNode>();
  nestedNodeMap = new Map<PermissionNode, PermissionFlatNode>();
  private treeData: PermissionNode[] = [];

  get f() { return this.roleForm.controls; }

  constructor(
    public roleService: RoleService,
    public roleComponent: RolesManagementMainComponent,
    public alertService: AlertService,
    public loader: LoaderService,
    private eventService: EventEmitterService,
    public formBuilder: UntypedFormBuilder,
    private permissionService: PermissionService,
    private readonly loaderService: LoaderService,
    public toastr: ToastrService,
    public cdr: ChangeDetectorRef) {

    this.treeControl = new FlatTreeControl<PermissionFlatNode>(
      node => node.level,
      node => node.expandable
    );

    this.treeFlattener = new MatTreeFlattener(
      this.transformer.bind(this),
      node => node.level,
      node => node.expandable,
      node => node.children
    );

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  transformer(node: PermissionNode, level: number): PermissionFlatNode {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.name === node.name
      ? existingNode
      : {} as PermissionFlatNode;
    flatNode.name = node.name;
    flatNode.displayName = node.displayName;
    flatNode.level = level;
    flatNode.expandable = !!(node.children && node.children.length > 0);
    flatNode.checked = node.isGranted;
    flatNode.indeterminate = false;
    flatNode.disabled = false;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  hasChild = (_: number, node: PermissionFlatNode) => node.expandable;

  ngOnInit() {
    this.roleForm = this.formBuilder.group({
      name: [null, Validators.required],
      isPublic: [false],
      isDefault: [false],
      isStatic: [false],
      isAdmin: [false],
      concurrencyStamp: [null],
      description: [null],
      isActive: [false],
      permission: [this.defaultPermissions]
    });
    this.closeEvent();
  }

  IsNullOrEmpty(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    return false;
  }

  /* istanbul ignore next */
  initMatComponent() {
    [].forEach.call(document.querySelectorAll('.text-material'), function (element) {
      if (element && element.querySelector('.mdc-text-field__input')) {
        textField.MDCTextField.attachTo(element);
      }
    });
    [].map.call(document.querySelectorAll('.ripple-material'), function (el) {
      ripple.MDCRipple.attachTo(el);
    });
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    setTimeout(() => {
      [].forEach.call(document.querySelectorAll('.text-material'), function (element) {
        if (element && element.querySelector('.mdc-text-field__input')) {
          textField.MDCTextField.attachTo(element);
        }
      });
      [].forEach.call(document.querySelectorAll('.ripple-material'), function (el) {
        if (el) {
          ripple.MDCRipple.attachTo(el);
        }
      });
      [].forEach.call(document.querySelectorAll('.formfield-material'), function (elem) {
        if (elem) {
          formField.MDCFormField.attachTo(elem);
        }
      });
    }, 2000);
  }

  on_paste_omit_special_char(event: ClipboardEvent, control) {
    const clipboardData = event.clipboardData;
    let filteredText = '';
    const pastedText = clipboardData.getData('text');
    let k;
    for (let i = 0; i < pastedText.length; i++) {
      k = pastedText.codePointAt(i);
      if (k === undefined) {
        continue;
      }
      if ((k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || k == 32 || (k >= 48 && k <= 57)) {
        filteredText += pastedText.charAt(i);
      }
    }
    this.roleForm.controls[control].setValue(filteredText);
    return false;
  }

  omit_special_char(event: KeyboardEvent): boolean {
    const key = typeof event.key === 'string' ? event.key : '';
    const k = (key ? key.codePointAt(0) : undefined) ?? event.charCode;
    if (k === undefined) {
      return false;
    }
    return ((k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || k == 32 || (k >= 48 && k <= 57));
  }

  onCheckChange(isActive: boolean): void {
    if (isActive) {
      this.roleForm.controls['isActive'].setValue(true);
    } else {
      this.roleForm.controls['isActive'].setValue(false);
    }
  }

  close() {
    this.submitted = false;
    this.roleForm.reset();
    this.closeSlider.emit();
  }

  closeEvent() {
    this.eventService.events$.forEach(_event => {
      this.close();
    });
  }

  save() {
    this.submitted = true;
    this.roleForm.value.isActive = this.roleForm.controls['isActive'].value;
    this.submitButton.nativeElement.disabled = true;

    this.collectSelectedPermissions();

    if (
      this.selectedPermissions &&
      this.selectedPermissions.includes('MANAGE.RELEASE.MODIFY') &&
      this.selectedPermissions.includes('MANAGE.RELEASE.STATUS.VIEW')
    ) {
      this.toastr.error(
        'You cannot select "Modify Release" and "View Release" permissions at the same time in Manage Releases Section.',
        'Invalid Permission Selection'
      );
      this.submitButton.nativeElement.disabled = false;
      return;
    }
    if (!this.selectedPermissions || this.selectedPermissions.length === 0) {
      this.toastr.error('Please select at least one permission', 'Error');
      this.submitButton.nativeElement.disabled = false;
      return;
    }
    const updatedPermissions = this.updatePermissions(this.defaultPermissions, this.selectedPermissions);
    this.roleForm.controls['permission'].setValue(updatedPermissions);
    if (this.roleForm.invalid) {
      return;
    }
    if (this.data) {
      this.roleService.apiAppRoleIdPut(this.data.id, this.roleForm.getRawValue())
        .subscribe({
          next: (_response) => {
            this.toastr.success('Role updated successfully', '');
            this.close();
            this.roleComponent.ngOnInit();
          },
          error: (error) => {
            if (error.error.error.code === '601') {
              this.alertService.buildDialog().fire({
                title: 'ERROR!', text: 'Role already exists',
                icon: this.alertService.ErrorIcon, showCancelButton: false,
                confirmButtonText: 'OK', cancelButtonText: '',
              });
            } else if (error.error.error.code === '604') {
              this.alertService.buildDialog().fire('ERROR!', 'Error While adding Permission', 'error');
            } else {
              this.alertService.buildDialog().fire({
                title: 'ERROR!', text: error.error.error.message,
                icon: this.alertService.ErrorIcon, showCancelButton: false,
                confirmButtonText: 'OK', cancelButtonText: '',
              });
            }
            this.close();
          }
        });
      this.initMatComponent();
    } else {
      this.roleForm.controls['isPublic'].setValue(false);
      this.roleForm.controls['isStatic'].setValue(false);
      this.roleForm.controls['isDefault'].setValue(false);
      this.roleForm.controls['isAdmin'].setValue(false);
      if (this.roleForm.value.isActive == null) {
        this.roleForm.controls['isActive'].setValue(false);
      }
      this.roleService.apiAppRolePost(this.roleForm.value)
        .subscribe({
          next: (_response) => {
            this.toastr.success('Role created successfully', '');
            this.close();
            this.roleComponent.ngOnInit();
          },
          error: (error) => {
            if (error.error.error.code === '601') {
              this.alertService.buildDialog().fire({
                title: 'ERROR!', text: 'Role already exists',
                icon: this.alertService.ErrorIcon, showCancelButton: false,
                confirmButtonText: 'OK', cancelButtonText: '',
              });
            } else if (error.error.error.code === '604') {
              this.alertService.buildDialog().fire('ERROR!', 'Permission Denied', 'error');
            } else {
              this.alertService.buildDialog().fire({
                title: 'ERROR!', text: error.error.error.message,
                icon: this.alertService.ErrorIcon, showCancelButton: false,
                confirmButtonText: 'OK', cancelButtonText: '',
              });
            }
            this.close();
          }
        });
    }
  }

  convert(array) {
    const map = {};
    for (const obj of array) {
      obj.children = [];
      map[obj.name] = obj;
      const parent = obj.parentName || '-';
      if (!map[parent]) {
        map[parent] = { children: [] };
      }
      map[parent].children.push(obj);
    }
    if (map['-'].children != undefined)
      return map['-'].children;
    else
      return [];
  }

  setLoader() {
    this.loading = true;
    this.loaderService.showSpinner.next(true);
  }

  setUnLoader() {
    this.loading = false;
    this.loaderService.showSpinner.next(false);
  }

  ngOnChanges(_changes: SimpleChanges) {
    if (this.data) {
      this.setLoader();
      setTimeout(() => {
        firstValueFrom(this.roleService.apiAppRoleIdGet(this.data.id)).then((res) => {
          if (res && res.id) {
            this.isAdminRoles = res.isAdmin;
            this.roleForm = this.formBuilder.group({
              concurrencyStamp: [res.concurrencyStamp],
              id: [res.id],
              name: [res.name, Validators.required],
              isPublic: [res.isPublic],
              isDefault: [res.isDefault],
              isStatic: [res.isStatic],
              isAdmin: [res.isAdmin],
              description: [res.description],
              isActive: [res.isActive],
              permission: [res.permission]
            });
          }
          if (res.isAdmin) {
            this.roleForm.get('name')?.disable();
          }
          this.isActive = res.isActive;
          if (res.permission) {
            this.getPermissions(res.permission);
          }
          this.setUnLoader();
        });
      }, 1000);
    } else {
      if (this.defaultPermissions && this.defaultPermissions.length > 0) {
        this.resetIsGranted(this.defaultPermissions);
        this.getPermissions(this.defaultPermissions);
      }
    }
  }

  getPermissions(permissions) {
    this.treeData = this.buildTreeData(permissions);
    this.dataSource.data = this.treeData;
    this.treeControl.collapseAll();

    if (!this.treeData || this.treeData.length === 0) {
      throw new Error('Treeview items became undefined or empty!');
    }

    // Restore checked state and compute indeterminate
    this.treeControl.dataNodes.forEach(node => {
      const nestedNode = this.flatNodeMap.get(node);
      if (nestedNode) {
        node.checked = nestedNode.isGranted;
      }
    });
    this.updateIndeterminateState();
    this.updateAllCheckedState();
    this.applyMutualExclusionState();
  }

  buildTreeData(permissionData: any[]): PermissionNode[] {
    if (!permissionData || !Array.isArray(permissionData)) return [];
    return permissionData.map(p => ({
      name: p.name,
      displayName: p.displayName,
      isGranted: p.isGranted,
      children: p.children && p.children.length > 0 ? this.buildTreeData(p.children) : undefined
    }));
  }

  // --- Check cascade logic ---
  toggleNode(node: PermissionFlatNode): void {
    node.checked = !node.checked;
    this.setDescendantsChecked(node, node.checked);
    this.updateAncestorsChecked(node);
    this.applyMutualExclusion(node);
    this.updateAllCheckedState();
    this.syncNestedNodeState();
  }

  setDescendantsChecked(node: PermissionFlatNode, checked: boolean): void {
    const descendants = this.treeControl.getDescendants(node);
    descendants.forEach(child => {
      if (!child.disabled) {
        child.checked = checked;
        child.indeterminate = false;
      }
    });
  }

  updateAncestorsChecked(node: PermissionFlatNode): void {
    let parent = this.getParentNode(node);
    while (parent) {
      const descendants = this.treeControl.getDescendants(parent);
      const allChecked = descendants.every(child => child.checked);
      const noneChecked = descendants.every(child => !child.checked && !child.indeterminate);
      parent.checked = allChecked;
      parent.indeterminate = !allChecked && !noneChecked;
      parent = this.getParentNode(parent);
    }
  }

  getParentNode(node: PermissionFlatNode): PermissionFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) return null;
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  updateIndeterminateState(): void {
    // Process from deepest level to top
    const maxLevel = Math.max(...this.treeControl.dataNodes.map(n => n.level), 0);
    for (let level = maxLevel; level >= 0; level--) {
      this.treeControl.dataNodes
        .filter(n => n.level === level && n.expandable)
        .forEach(parent => {
          const descendants = this.treeControl.getDescendants(parent);
          if (descendants.length === 0) return;
          const allChecked = descendants.every(c => c.checked);
          const noneChecked = descendants.every(c => !c.checked && !c.indeterminate);
          parent.checked = allChecked;
          parent.indeterminate = !allChecked && !noneChecked;
        });
    }
  }

  // --- Select All ---
  toggleAll(): void {
    this.allChecked = !this.allChecked;
    this.allIndeterminate = false;
    this.treeControl.dataNodes.forEach(node => {
      if (!node.disabled) {
        node.checked = this.allChecked;
        node.indeterminate = false;
      }
    });
    this.applyMutualExclusionState();
    this.updateIndeterminateState();
    this.updateAllCheckedState();
    this.syncNestedNodeState();
  }

  updateAllCheckedState(): void {
    const nodes = this.treeControl.dataNodes;
    if (!nodes || nodes.length === 0) return;
    const allChecked = nodes.every(n => n.checked);
    const noneChecked = nodes.every(n => !n.checked && !n.indeterminate);
    this.allChecked = allChecked;
    this.allIndeterminate = !allChecked && !noneChecked;
  }

  // --- Filter ---
  applyFilter(): void {
    // Snapshot checked names before clearing maps (stale cloned entries would desync state)
    const checkedNames = this.getCheckedNames();
    this.flatNodeMap.clear();
    this.nestedNodeMap.clear();

    if (!this.filterText || this.filterText.trim() === '') {
      this.dataSource.data = this.treeData;
    } else {
      const filtered = this.filterTree(this.treeData, this.filterText.toLowerCase());
      this.dataSource.data = filtered;
      this.treeControl.expandAll();
    }
    this.restoreCheckedStateFromNames(checkedNames);
  }

  private getCheckedNames(): Set<string> {
    const names = new Set<string>();
    this.flatNodeMap.forEach((_nested, flat) => {
      if (flat.checked && flat.name) names.add(flat.name);
    });
    return names;
  }

  private restoreCheckedStateFromNames(checkedNames: Set<string>): void {
    this.treeControl.dataNodes.forEach(node => {
      node.checked = checkedNames.has(node.name);
    });
    this.updateIndeterminateState();
    this.updateAllCheckedState();
    this.applyMutualExclusionState();
  }

  filterTree(nodes: PermissionNode[], filter: string): PermissionNode[] {
    return nodes.reduce<PermissionNode[]>((result, node) => {
      const children = node.children ? this.filterTree(node.children, filter) : [];
      if (node.displayName.toLowerCase().includes(filter) || children.length > 0) {
        result.push({ ...node, children: children.length > 0 ? children : node.children });
      }
      return result;
    }, []);
  }

  restoreCheckedState(): void {
    const checkedNames = new Set<string>();
    this.flatNodeMap.forEach((nested, flat) => {
      if (flat.checked) checkedNames.add(flat.name);
    });
    this.treeControl.dataNodes.forEach(node => {
      node.checked = checkedNames.has(node.name);
    });
    this.updateIndeterminateState();
    this.updateAllCheckedState();
  }

  // --- Mutual exclusion (MODIFY vs VIEW) ---
  applyMutualExclusion(changedNode: PermissionFlatNode): void {
    const parent = this.getParentNode(changedNode);
    if (!parent) return;
    const siblings = this.treeControl.getDescendants(parent)
      .filter(n => n.level === changedNode.level);

    if (changedNode.name === 'MANAGE.RELEASE.MODIFY' && changedNode.checked) {
      const viewNode = siblings.find(s => s.name === 'MANAGE.RELEASE.STATUS.VIEW');
      if (viewNode) { viewNode.disabled = true; viewNode.checked = false; viewNode.indeterminate = false; }
    } else if (changedNode.name === 'MANAGE.RELEASE.MODIFY' && !changedNode.checked) {
      const viewNode = siblings.find(s => s.name === 'MANAGE.RELEASE.STATUS.VIEW');
      if (viewNode) { viewNode.disabled = false; }
    }

    if (changedNode.name === 'MANAGE.RELEASE.STATUS.VIEW' && changedNode.checked) {
      const modifyNode = siblings.find(s => s.name === 'MANAGE.RELEASE.MODIFY');
      if (modifyNode) { modifyNode.disabled = true; modifyNode.checked = false; modifyNode.indeterminate = false; }
    } else if (changedNode.name === 'MANAGE.RELEASE.STATUS.VIEW' && !changedNode.checked) {
      const modifyNode = siblings.find(s => s.name === 'MANAGE.RELEASE.MODIFY');
      if (modifyNode) { modifyNode.disabled = false; }
    }

    this.updateAncestorsChecked(changedNode);
  }

  applyMutualExclusionState(): void {
    const nodes = this.treeControl.dataNodes;
    const modifyNode = nodes.find(n => n.name === 'MANAGE.RELEASE.MODIFY');
    const viewNode = nodes.find(n => n.name === 'MANAGE.RELEASE.STATUS.VIEW');
    if (modifyNode && viewNode) {
      // If both are checked (e.g. from "select all"), uncheck the View node
      if (modifyNode.checked && viewNode.checked) {
        viewNode.checked = false;
        viewNode.disabled = true;
        viewNode.indeterminate = false;
      } else if (modifyNode.checked) {
        viewNode.disabled = true;
      } else if (viewNode.checked) {
        modifyNode.disabled = true;
      } else {
        modifyNode.disabled = false;
        viewNode.disabled = false;
      }
    }
  }

  // --- Sync flat node state back to nested nodes ---
  syncNestedNodeState(): void {
    this.flatNodeMap.forEach((nestedNode, flatNode) => {
      nestedNode.isGranted = flatNode.checked;
    });
  }

  // --- Collect selected permissions ---
  collectSelectedPermissions(): void {
    const checkedNames = new Set<string>();
    const visibleNames = new Set<string>();

    // Visible nodes are authoritative for their checked state
    if (this.treeControl.dataNodes) {
      this.treeControl.dataNodes.forEach(node => {
        visibleNames.add(node.name);
        if (node.checked) {
          checkedNames.add(node.name);
        }
      });
    }

    // Preserve checked state for nodes hidden by the active filter
    this.flatNodeMap.forEach((_nestedNode, flatNode) => {
      if (!visibleNames.has(flatNode.name) && flatNode.checked && flatNode.name) {
        checkedNames.add(flatNode.name);
      }
    });

    this.selectedPermissions = Array.from(checkedNames);
  }

  updatePermissions(features: any[], permissions: string[]): any[] {
    const updateRecursive = (nodes: any[]) => {
      nodes.forEach(node => {
        node.isGranted = permissions.includes(node.name);
        if (node.children && node.children.length > 0) {
          updateRecursive(node.children);
        }
      });
    };
    updateRecursive(features);
    return features;
  }

  onFilterChange(_value: string) {
    //Intentional
  }

  noop() { }

  resetIsGranted(permissions: any[]) {
    permissions.forEach(p => {
      p.isGranted = false;
      if (p.children && p.children.length > 0) {
        this.resetIsGranted(p.children);
      }
    });
  }
}
